import { expandedLayouts } from './expanded-venues';
import { normalizeLabel, type SeatCoordinateInput } from './seat-identity';
export type SeatLayoutPrecision = 'seat' | 'section' | 'booking';

export interface SeatLayoutSource {
  label: string;
  url: string;
  checkedAt: string;
}

export interface SeatLayoutRow {
  label: string;
  seats: number[];
  gapBefore?: boolean;
  accessibleSeats?: number[];
  aisleAfter?: number[];
}

export interface SeatLayoutSection {
  id: string;
  label: string;
  shortLabel: string;
  position: 'left' | 'center' | 'right' | 'full';
  rows?: SeatLayoutRow[];
  seatCount?: number;
}

export interface SeatLayoutFloor {
  id: string;
  label: string;
  seatCount?: number;
  sections: SeatLayoutSection[];
}

export interface SeatLayoutDefinition {
  venueId: string;
  spaceId: string;
  layoutVersionId: string;
  precision: SeatLayoutPrecision;
  surfaceLabel: '무대' | '스크린';
  title: string;
  note: string;
  source: SeatLayoutSource;
  floors: SeatLayoutFloor[];
}

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

function row(label: number, from: number, to: number, options?: Pick<SeatLayoutRow, 'gapBefore' | 'accessibleSeats'>): SeatLayoutRow {
  return { label: String(label), seats: range(from, to), ...options };
}

function numberedRows(counts: number[], options?: { gapBefore?: number[]; accessibleLastRow?: boolean }): SeatLayoutRow[] {
  let nextSeat = 1;
  return counts.map((count, index) => {
    const seats = range(nextSeat, nextSeat + count - 1);
    nextSeat += count;
    const result: SeatLayoutRow = { label: String(index + 1), seats };
    if (options?.gapBefore?.includes(index + 1)) result.gapBefore = true;
    if (options?.accessibleLastRow && index === counts.length - 1) result.accessibleSeats = seats;
    return result;
  });
}

function splitRow(
  label: number,
  counts: [number, number, number],
  options?: Pick<SeatLayoutRow, 'gapBefore'> & { leftEnd?: number; centerStart?: number; rightStart?: number },
): SeatLayoutRow {
  const leftEnd = options?.leftEnd ?? 15;
  const centerStart = options?.centerStart ?? 16;
  const rightStart = options?.rightStart ?? 34;
  const left = range(leftEnd - counts[0] + 1, leftEnd);
  const center = range(centerStart, centerStart + counts[1] - 1);
  const right = range(rightStart, rightStart + counts[2] - 1);
  return {
    label: String(label),
    seats: [...left, ...center, ...right],
    aisleAfter: [leftEnd, centerStart + counts[1] - 1],
    ...(options?.gapBefore ? { gapBefore: true } : {}),
  };
}

const checkedAt = '2026-08-12';

/**
 * 원본 좌석 이미지는 저장·복제·embed하지 않고 공식 원문 페이지로만 연결해요.
 * 시설이 공개한 좌석 수와 접근 가능한 열·번호 설명만 제품 좌표로 옮겨요.
 */
export const seatLayouts: SeatLayoutDefinition[] = [
  {
    venueId: 'sejong-center',
    spaceId: 'm-theater',
    layoutVersionId: 'LV-sejong-center-m-theater-official-20260812',
    precision: 'seat',
    surfaceLabel: '무대',
    title: 'M씨어터 실제 좌석도',
    note: '공식 좌석배치도의 두 층과 열·번호를 옮겼어요. 2층 무대 양옆 특수석 10석처럼 일반 열로 특정할 수 없는 자리는 공식 원본과 티켓 표기를 확인해 주세요. 공식 총계 314석과 상세 설명 313석의 1석 차이도 그대로 고지해요.',
    source: {
      label: '세종문화회관 M씨어터 좌석배치도',
      url: 'https://www.sejongpac.or.kr/portal/main/contents.do?menuNo=200369',
      checkedAt,
    },
    floors: [
      {
        id: '1f',
        label: '1층',
        seatCount: 295,
        sections: [
          {
            id: '1f-a', label: '1층 A구역', shortLabel: 'A', position: 'left',
            rows: [
              row(1, 1, 4), row(2, 1, 4), row(3, 1, 5, { gapBefore: true }), row(4, 1, 5),
              row(5, 1, 6), row(6, 1, 6), row(7, 1, 6), row(8, 1, 6),
              row(9, 1, 3, { gapBefore: true, accessibleSeats: [1, 2, 3] }), row(10, 1, 5),
              row(11, 1, 4), row(12, 1, 3), row(13, 1, 4),
            ],
          },
          {
            id: '1f-b', label: '1층 B구역', shortLabel: 'B', position: 'center',
            rows: [
              row(1, 5, 21), row(2, 5, 21), row(3, 6, 22, { gapBefore: true }), row(4, 6, 21),
              row(5, 7, 23), row(6, 7, 22), row(7, 7, 23), row(8, 7, 22),
              row(9, 4, 18, { gapBefore: true }), row(10, 6, 19), row(11, 5, 14),
            ],
          },
          {
            id: '1f-c', label: '1층 C구역', shortLabel: 'C', position: 'right',
            rows: [
              row(1, 22, 25), row(2, 22, 25), row(3, 23, 27, { gapBefore: true }), row(4, 22, 26),
              row(5, 24, 29), row(6, 23, 28), row(7, 24, 29), row(8, 23, 28),
              row(9, 84, 87, { gapBefore: true, accessibleSeats: [84, 85, 86, 87] }), row(10, 20, 24),
              row(11, 15, 18), row(12, 4, 6), row(13, 5, 8),
            ],
          },
        ],
      },
      {
        id: '2f', label: '2층', seatCount: 313,
        sections: [{
          id: '2f-a', label: '2층 A구역', shortLabel: 'A', position: 'center',
          rows: [
            row(1, 1, 30), row(2, 1, 26, { gapBefore: true }),
            row(3, 1, 30), row(4, 1, 30), row(5, 1, 30), row(6, 1, 30), row(7, 1, 30),
            row(8, 1, 30), row(9, 1, 30), row(10, 1, 24), row(11, 1, 13),
          ],
          seatCount: 303,
        }],
      },
    ],
  },
  {
    venueId: 'sejong-center',
    spaceId: 'grand-theater',
    layoutVersionId: 'LV-sejong-center-grand-theater-official-20260812',
    precision: 'seat',
    surfaceLabel: '무대',
    title: '세종대극장 실제 좌석도',
    note: '공식 페이지가 공개한 층·구역별 열과 누적 번호를 클릭할 수 있게 옮겼어요. 원본 설명의 오탈자 가능성이 있어 티켓 표기를 마지막으로 확인해 주세요.',
    source: {
      label: '세종문화회관 대극장 좌석배치도',
      url: 'https://www.sejongpac.or.kr/portal/main/contents.do?menuNo=200368',
      checkedAt,
    },
    floors: [
      {
        id: '1f', label: '1층', seatCount: 1030,
        sections: [
          officialSection('1층', 'A', 'left', [5, 6, 7, 8, 9, 10, 11, 12, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13]),
          officialSection('1층', 'B', 'left', [...Array(18).fill(12), 5], { accessibleLastRow: true }),
          officialSection('1층', 'C', 'center', Array(16).fill(12)),
          officialSection('1층', 'D', 'right', [...Array(18).fill(12), 5], { accessibleLastRow: true }),
          officialSection('1층', 'E', 'right', [5, 6, 7, 8, 9, 10, 11, 12, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13]),
        ],
      },
      {
        id: '2f', label: '2층', seatCount: 964,
        sections: [
          officialSection('2층', 'A', 'left', [4, 5, 4, 4, 5, 5, 6, 7, 8, 8, 9, 9, 9, 5], { accessibleLastRow: true }),
          officialSection('2층', 'B', 'left', Array(18).fill(8)),
          officialSection('2층', 'C', 'left', [13, 13, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8]),
          officialSection('2층', 'D', 'center', [10, 10, 10, 10, 10, 16, 20, 18, 18, 18, 18, 20, 4, 4, 2, 2, 2]),
          officialSection('2층', 'E', 'right', [13, 13, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8]),
          officialSection('2층', 'F', 'right', Array(18).fill(8)),
          officialSection('2층', 'G', 'right', [4, 5, 4, 4, 5, 5, 6, 7, 8, 8, 9, 9, 9, 5], { accessibleLastRow: true }),
        ],
      },
      {
        id: '3f', label: '3층', seatCount: 1028,
        sections: sectionFan('3층', ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']),
      },
    ],
  },
  {
    venueId: 'sejong-center',
    spaceId: 's-theater',
    layoutVersionId: 'LV-sejong-center-s-theater-official-20260812',
    precision: 'seat',
    surfaceLabel: '무대',
    title: 'S씨어터 실제 좌석도',
    note: '공식 페이지가 공개한 중앙 좌석의 열·번호를 클릭할 수 있게 옮겼어요. L·R·OP 특수열은 티켓 표기를 보고 직접 입력해 주세요.',
    source: {
      label: '세종문화회관 S씨어터 좌석배치도',
      url: 'https://www.sejongpac.or.kr/portal/main/contents.do?menuNo=200371',
      checkedAt,
    },
    floors: [
      {
        id: '1f', label: '1층', seatCount: 196,
        sections: [{
          id: '1층-중앙', label: '1층 중앙', shortLabel: '중앙', position: 'center', seatCount: 152,
          rows: [
            row(1, 1, 17), row(2, 18, 34), row(3, 35, 51),
            row(4, 1, 11, { accessibleSeats: [1, 2, 10, 11] }),
            row(5, 1, 15), row(6, 1, 15), row(7, 1, 15), row(8, 1, 15), row(9, 1, 15), row(10, 1, 15),
          ],
        }],
      },
      {
        id: '2f', label: '2층', seatCount: 132,
        sections: [{
          id: '2층-중앙', label: '2층 중앙', shortLabel: '중앙', position: 'center', seatCount: 100,
          rows: [row(1, 1, 20), row(2, 1, 20), row(3, 1, 20), row(4, 1, 20), row(5, 1, 20)],
        }],
      },
    ],
  },
  {
    venueId: 'blue-square',
    spaceId: 'shinhan-card-hall',
    layoutVersionId: 'LV-blue-square-main-hall-official-20260812',
    precision: 'seat',
    surfaceLabel: '무대',
    title: '우리은행홀 실제 좌석도',
    note: '현재 공식 명칭은 우리은행홀이에요. 공식 좌석 배치의 층별 굴곡·통로를 나누고 1·2층 일반석은 열·번호로 선택할 수 있어요. 1층 특수석과 좌표 전사가 끝나지 않은 3층은 공식 원본과 티켓 표기를 확인해 주세요.',
    source: {
      label: '블루스퀘어 우리은행홀 시설 안내',
      url: 'https://www.bluesquare.kr/pages/intro/fac.php?tab=shinhan-card',
      checkedAt,
    },
    floors: [
      {
        id: '1f', label: '1층', seatCount: 1066,
        sections: [{
          id: '1층-전체', label: '1층', shortLabel: '전체', position: 'center',
          rows: [
            ...[8, 9, 10, 10, 11, 12, 13].map((leftRight, index) => splitRow(index + 1, [leftRight, index % 2 ? 17 : 16, leftRight])),
            ...[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map((label, index) => splitRow(label, [15, index % 2 ? 17 : 16, 15], { gapBefore: label === 8 })),
          ],
        }],
      },
      {
        id: '2f', label: '2층', seatCount: 430,
        sections: [{
          id: '2층-전체', label: '2층', shortLabel: '전체', position: 'center',
          rows: [15, 15, 15, 15, 15, 13, 13, 13, 13, 15].map((sideCount, index) => splitRow(
            index + 1,
            [sideCount, index < 5 ? 16 : index === 5 ? 14 : 13, sideCount],
            { rightStart: 32 },
          )),
        }],
      },
      {
        id: '3f', label: '3층', seatCount: 270,
        sections: sectionFan('3층', ['좌측', '중앙', '우측']),
      },
    ],
  },
  {
    venueId: 'blue-square',
    spaceId: 'mastercard-hall',
    layoutVersionId: 'LV-blue-square-concert-hall-configurable-20260812',
    precision: 'section',
    surfaceLabel: '무대',
    title: '우리WON뱅킹홀 공식 배치 참고',
    note: '이 홀은 좌석형과 스탠딩형으로 구성이 바뀌어요. 공식 이미지는 층별로 보여주되, 회차별 판매 좌석과 번호가 달라질 수 있어 구역까지만 선택하고 티켓 좌표를 직접 입력해 주세요.',
    source: {
      label: '블루스퀘어 우리WON뱅킹홀 시설 안내',
      url: 'https://www.bluesquare.kr/pages/intro/fac.php?tab=master-card',
      checkedAt,
    },
    floors: [
      {
        id: '1f', label: '1층 좌석형', seatCount: 998,
        sections: sectionFan('1층', ['A', 'B', 'C', 'D', 'E']),
      },
      {
        id: '2f', label: '2층', seatCount: 381,
        sections: sectionFan('2층', ['좌측', '중앙', '우측']),
      },
      {
        id: 'standing', label: '스탠딩형',
        sections: sectionFan('스탠딩', ['A', 'B', 'C', 'D']),
      },
    ],
  },
  bookingLayout(
    'cgv-yongsan',
    'imax-laser',
    '용산 IMAX 상영 회차 좌석도',
    'https://m.cgv.co.kr/WebAPP/TheaterV4/TheaterDetail.aspx?tc=P013',
    'CGV 용산아이파크몰 공식 극장 안내',
  ),
  bookingLayout(
    'cgv-yongsan',
    '4dx-screen',
    '용산 4DX SCREEN 상영 회차 좌석도',
    'https://m.cgv.co.kr/WebAPP/TheaterV4/TheaterDetail.aspx?tc=P013',
    'CGV 용산아이파크몰 공식 극장 안내',
  ),
  bookingLayout(
    'lotte-cinema-worldtower',
    'superplex',
    '월드타워 SUPER PLEX 상영 회차 좌석도',
    'https://www.lottecinema.co.kr/',
    '롯데시네마 공식 예매',
  ),
  bookingLayout(
    'lotte-cinema-worldtower',
    'charlotte',
    '월드타워 CHARLOTTE 상영 회차 좌석도',
    'https://www.lottecinema.co.kr/',
    '롯데시네마 공식 예매',
  ),
];

function sectionFan(floor: string, labels: string[]): SeatLayoutSection[] {
  return labels.map((label, index) => ({
    id: `${floor}-${label}`,
    label: `${floor} ${label}구역`,
    shortLabel: label,
    position: index < Math.floor(labels.length / 2) ? 'left' : index > Math.floor(labels.length / 2) ? 'right' : 'center',
  }));
}

function officialSection(
  floor: string,
  label: string,
  position: SeatLayoutSection['position'],
  rowCounts: number[],
  options?: { accessibleLastRow?: boolean },
): SeatLayoutSection {
  return {
    id: `${floor}-${label}`,
    label: `${floor} ${label}${label === '중앙' || label.endsWith('측') ? '' : '구역'}`,
    shortLabel: label,
    position,
    seatCount: rowCounts.reduce((sum, count) => sum + count, 0),
    rows: numberedRows(rowCounts, {
      gapBefore: [8],
      ...(options?.accessibleLastRow ? { accessibleLastRow: true } : {}),
    }),
  };
}

function bookingLayout(venueId: string, spaceId: string, title: string, url: string, label: string): SeatLayoutDefinition {
  return {
    venueId,
    spaceId,
    layoutVersionId: `LV-${venueId}-${spaceId}-booking-required`,
    precision: 'booking',
    surfaceLabel: '스크린',
    title,
    note: '영화관 좌석도는 날짜·영화·상영 회차를 고른 뒤 예매 단계에서 열려요. 고정 배치를 추측하지 않고, 공식 좌석도를 보면서 좌표를 옮겨 적어요.',
    source: { label, url, checkedAt },
    floors: [],
  };
}

seatLayouts.push(...expandedLayouts);

export function seatLayoutFor(venueId: string, spaceId: string): SeatLayoutDefinition | undefined {
  return seatLayouts.find((layout) => layout.venueId === venueId && layout.spaceId === spaceId);
}

/** Resolve typed facility coordinates without asserting a performance's stage layout. */
export function resolveLayoutSeat(layout: SeatLayoutDefinition, input: SeatCoordinateInput) {
  const sectionCode = input.sectionCode.trim();
  const rowLabel = normalizeLabel(input.rowLabel).replace(/열$/, '');
  const seatNumber = Number(input.seatNumber);
  if (!sectionCode || !rowLabel || !Number.isInteger(seatNumber) || seatNumber < 1 || seatNumber > 9999) return null;
  if (layout.precision === 'booking') return { sectionCode, rowLabel, seatNumber };
  const label = normalizeLabel(sectionCode);
  const sections = layout.floors.flatMap((floor) => floor.sections.filter((section) =>
    normalizeLabel(section.id) === label || normalizeLabel(section.label) === label
    || (floor.sections.length === 1 && normalizeLabel(floor.label) === label),
  ));
  const section = sections[0];
  if (sections.length !== 1 || !section) return null;
  if (section.rows && !section.rows.some((row) => normalizeLabel(row.label) === rowLabel && row.seats.includes(seatNumber))) return null;
  return { sectionCode: section.label, rowLabel, seatNumber };
}
